import React, { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '@/hooks';
import { Button } from '@/ui/button';
import { Filter as FilterIcon } from 'lucide-react';


const Filters = ( {onFilterChange} ) => {
    const isMobile = useIsMobile();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState([]);
    const [selectedPipeline, setSelectedPipeline] = useState([1,2,3,4,5]);
    const [isPipelineCleared, setIsPipelineCleared] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedSource, setSelectedSource] = useState([]);
    const [selectedLevel, setSelectedLevel] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState([]);

    // Keep a stable ref to onFilterChange so it never triggers the useEffect
    const onFilterChangeRef = useRef(onFilterChange);
    useEffect(() => {
        onFilterChangeRef.current = onFilterChange;
    });

    const productsOptions = [
        { id: 1, label: 'Remittance' },
        { id: 2, label: 'Insurance' },
        { id: 3, label: 'Re-KYC' },
        { id: 4, label: 'Referral' },
        { id: 5, label: 'Financing' },
    ];
    const pipelineStage = [
        { id: 1, label: 'Raw' },
        { id: 2, label: 'Fresh' },
        { id: 3, label: 'Profile Completed' },
        { id: 4, label: 'Meeting Completed' },
        { id: 5, label: 'Onboarded' },
        { id: 6, label: 'Activated' },
        { id: 7, label: 'Rejected' }
    ];
    const leadStatus = [
        { id: 0, label: 'Active' },
        { id: 1, label: 'Ongoing' },
        { id: 2, label: 'Inactive' },
        { id: 3, label: 'Tanked' }
    ];
    const leadSource = [
        { id: 1, label:"Self-Generated" },
        { id: 2, label:"Referral Hunter" },
        { id: 3, label:"Referral MM Employee" },
        { id: 4, label:"Referral Public" },
        { id: 5, label:"Inbound" },
        { id: 6, label:"Boost" },
        { id: 7, label:"Referral Active" },
        { id: 8, label:"Webscraping" },
        { id: 9, label:"Lazada" },
        { id: 10, label:"Bytedance" },
        { id: 11, label:"Referral Margin Sharing" },
        { id: 12, label:"Referral Active - Legacy" },
        { id: 13, label:"Referral Hunter - Active" },
        { id: 14, label:"Referral F&F" },
        { id: 15, label:"Referral Special" }
    ];
    const interestLevel = [
        { id: 1, label: 'Cold' },
        { id: 2, label: 'Warm' },
        { id: 3, label: 'Hot' }
    ];
    const region = [
        { id: 1, label: 'MY' },
        { id: 2, label: 'SG' },
        { id: 3, label: 'BN' },
        { id: 4, label: 'AU' }
    ];

    const handleToggleProduct = (optionId) => {
        setSelectedProduct(prev => 
            prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
        );
    };

    const handleTogglePipeline = (optionId) => {
        setIsPipelineCleared(false);
        setSelectedPipeline(prev => 
            prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
        );
    };

    const handleToggleStatus = (optionId) => {
        setSelectedStatus(prev =>
            prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
        );
    };

    const handleToggleSource = (optionId) => {
        setSelectedSource(prev =>
            prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
        );
    };

    const handleToggleLevel = (optionId) => {
        setSelectedLevel(prev =>
            prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
        );
    };

    const handleToggleRegion = (optionId) => {
        setSelectedRegion(prev =>
            prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
        );
    };

    const Dropdown = ({ label, selected, options, handleToggle, onClear }) => (
        <div className="relative group">
            <button
                className="px-4 py-2 bg-transparent hover:bg-yellow-100 rounded-lg
                           flex items-center space-x-2 font-medium text-gray-700
                           transition-colors duration-200"
            >
                <span>{label}</span>
                {selected.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 
                                    bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                        {selected.length}
                    </span>
                )}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
            </button>

            <div className="absolute left-0 mt-2 w-56 bg-white/80 backdrop-blur-sm border border-gray-200 
                            rounded-lg shadow-lg py-2 z-50 invisible group-hover:visible
                            transition-all duration-200 opacity-0 group-hover:opacity-100
                            max-h-80 overflow-y-auto">
                {options.map((option) => (
                    <label key={option.id} className="flex items-center px-4 py-2 hover:bg-yellow-100 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={selected.includes(option.id)}
                            onChange={() => handleToggle(option.id)}
                            className="h-4 w-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500 cursor-pointer"
                        />
                        <span className="ml-3 text-gray-700">{option.label}</span>
                    </label>
                ))}

                {selected.length > 0 && (
                    <div className="border-t border-gray-200 mt-2 pt-2 px-4">
                        <button onClick={onClear} className="text-sm text-yellow-600 hover:text-yellow-700">
                            Clear all
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    // Live filter — fires on every selection change, but onFilterChange is
    // accessed via ref so it never itself triggers this effect
    useEffect(() => {
        onFilterChangeRef.current({
            products: selectedProduct,
            pipelineStage: selectedPipeline,
            leadStatus: selectedStatus,
            leadSource: selectedSource,
            interestLevel: selectedLevel,
            region: selectedRegion,
        });
    }, [selectedProduct, selectedPipeline, isPipelineCleared, selectedStatus, selectedSource, selectedLevel, selectedRegion]);

    // Count total active filters
    const activeFilterCount = selectedProduct.length + selectedPipeline.length +
                              selectedStatus.length + selectedSource.length +
                              selectedLevel.length + selectedRegion.length;

    const clearAllFilters = () => {
        setSelectedProduct([]);
        setSelectedPipeline([1,2,3,4,5]);
        setIsPipelineCleared(false);
        setSelectedStatus([]);
        setSelectedSource([]);
        setSelectedLevel([]);
        setSelectedRegion([]);
    };

    // Mobile Filter Drawer
    if (isMobile) {
        return (
            <>
                <Button
                    onClick={() => setIsFilterOpen(true)}
                    className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300"
                >
                    <FilterIcon className="w-4 h-4 mr-2" />
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 
                                        bg-yellow-400 text-black text-xs font-bold rounded-full">
                            {activeFilterCount}
                        </span>
                    )}
                </Button>

                {isFilterOpen && (
                    <div className="fixed inset-0 z-[100] bg-white flex flex-col">
                        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                            <h2 className="text-lg font-bold">Filters</h2>
                            <button onClick={() => setIsFilterOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
                            <div>
                                <h3 className="font-bold text-base mb-3">Product</h3>
                                <div className="space-y-2">
                                    {productsOptions.map((option) => (
                                        <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={selectedProduct.includes(option.id)} onChange={() => handleToggleProduct(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                                            <span className="ml-3 text-gray-900">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-base mb-3">Pipeline Stage</h3>
                                <div className="space-y-2">
                                    {pipelineStage.map((option) => (
                                        <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={selectedPipeline.includes(option.id)} onChange={() => handleTogglePipeline(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                                            <span className="ml-3 text-gray-900">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-base mb-3">Lead Status</h3>
                                <div className="space-y-2">
                                    {leadStatus.map((option) => (
                                        <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={selectedStatus.includes(option.id)} onChange={() => handleToggleStatus(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                                            <span className="ml-3 text-gray-900">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-base mb-3">Source</h3>
                                <div className="space-y-2">
                                    {leadSource.map((option) => (
                                        <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={selectedSource.includes(option.id)} onChange={() => handleToggleSource(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                                            <span className="ml-3 text-gray-900">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-base mb-3">Interest Level</h3>
                                <div className="space-y-2">
                                    {interestLevel.map((option) => (
                                        <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={selectedLevel.includes(option.id)} onChange={() => handleToggleLevel(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                                            <span className="ml-3 text-gray-900">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-base mb-3">Region</h3>
                                <div className="space-y-2">
                                    {region.map((option) => (
                                        <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={selectedRegion.includes(option.id)} onChange={() => handleToggleRegion(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                                            <span className="ml-3 text-gray-900">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-2">
                            <Button onClick={clearAllFilters} variant="outline" className="flex-1">
                                Clear All
                            </Button>
                            <Button
                                onClick={() => setIsFilterOpen(false)}
                                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black"
                            >
                                Apply Filters
                            </Button>
                        </div>
                    </div>
                )}
            </>
        );
    }

    // Desktop Filter Layout
    return (
        <div>
            <h1 className="font-bold pb-4">Quick Filter</h1>
            <div className="flex flex-row justify-between items-center">
                <p className="font-bold text-gray-500">Filter by:</p>
                <Dropdown
                    label="Product"
                    selected={selectedProduct}
                    options={productsOptions}
                    handleToggle={handleToggleProduct}
                    onClear={() => setSelectedProduct([])}
                />
                <Dropdown
                    label="Pipeline Stage"
                    selected={selectedPipeline}
                    options={pipelineStage}
                    handleToggle={handleTogglePipeline}
                    onClear={() => setSelectedPipeline([])}
                />
                <Dropdown
                    label="Lead Status"
                    selected={selectedStatus}
                    options={leadStatus}
                    handleToggle={handleToggleStatus}
                    onClear={() => setSelectedStatus([])}
                />
                <Dropdown
                    label="Source"
                    selected={selectedSource}
                    options={leadSource}
                    handleToggle={handleToggleSource}
                    onClear={() => setSelectedSource([])}
                />
                <Dropdown
                    label="Interest Level"
                    selected={selectedLevel}
                    options={interestLevel}
                    handleToggle={handleToggleLevel}
                    onClear={() => setSelectedLevel([])}
                />
                <Dropdown
                    label="Region"
                    selected={selectedRegion}
                    options={region}
                    handleToggle={handleToggleRegion}
                    onClear={() => setSelectedRegion([])}
                />
                <button
                    onClick={clearAllFilters}
                    className="px-4 py-2 bg-transparent hover:bg-yellow-100 rounded-lg
                               flex items-center space-x-2 font-bold text-gray-700
                               underline transition-colors duration-200"
                >
                    Clear Filters
                </button>
            </div>
        </div>
    );
};

export default Filters;